import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Tabs, Tab,
  Alert, CircularProgress, Chip, LinearProgress, CardHeader,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { budgetsApi } from '../services/api';
import { formatMoney } from '../utils/money';

function BudgetAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [activeView, setActiveView] = useState(0); // 0: Monthly, 1: Quarterly, 2: Yearly
  const [selectedTrendCategory, setSelectedTrendCategory] = useState(0); // Index of selected category for trend

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await budgetsApi.getComprehensiveAnalytics();
      setAnalytics(response.data.analytics);
      setError(null);
    } catch (err) {
      console.error('Error loading budget analytics:', err);
      setError('Failed to load budget analytics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'safe': return '#4caf50';
      case 'warning': return '#ff9800';
      case 'critical': return '#ff5722';
      case 'exceeded': return '#f44336';
      default: return '#2196f3';
    }
  };

  const getPaceColor = (paceStatus) => {
    switch (paceStatus) {
      case 'ahead': return '#4caf50';
      case 'on_track': return '#2196f3';
      case 'behind': return '#f44336';
      default: return '#757575';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!analytics || !analytics.monthly || analytics.monthly.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1" color="text.secondary" align="center">
            No budget data available. Create budgets in Settings to see analytics.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const currentData = activeView === 0 ? analytics.monthly : 
                       activeView === 1 ? analytics.quarterly : 
                       analytics.yearly;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Budget Analytics
      </Typography>

      {/* Period Tabs */}
      <Card sx={{ mb: 2 }}>
        <Tabs 
          value={activeView} 
          onChange={(_, newValue) => setActiveView(newValue)}
          variant="fullWidth"
        >
          <Tab label="Monthly" />
          <Tab label="Quarterly" />
          <Tab label="Yearly" />
        </Tabs>
      </Card>

      {/* Row 1: Overview Cards + Budget vs Actual Chart */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Period Overview Cards */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: 500, width: '100%' }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  {activeView === 0 ? 'Current Month' : activeView === 1 ? 'Current Quarter' : 'Current Year'} Overview
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 2, overflow: 'hidden' }}>
              <Box 
                sx={{ 
                  height: '100%', 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(6, 182, 212, 0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(6, 182, 212, 0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(6, 182, 212, 0.5)',
                    },
                  },
                }}
              >
                {currentData.map((budget) => (
                  <Box key={budget.id} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="600">
                          {budget.name || budget.category}
                        </Typography>
                        <Chip 
                          label={budget.category || 'Overall'}
                          size="small"
                          color={budget.category ? 'primary' : 'success'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Chip 
                        label={budget.status.toUpperCase()}
                        size="small"
                        sx={{ 
                          backgroundColor: getStatusColor(budget.status),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                    </Box>
                    
                    {budget.accountIds && budget.accountIds.length > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Accounts: {budget.accountIds.join(', ')}
                      </Typography>
                    )}
                    
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(budget.percentage, 100)}
                      sx={{ 
                        my: 1, 
                        height: 8, 
                        borderRadius: 1,
                        backgroundColor: 'rgba(6, 182, 212, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getStatusColor(budget.status),
                        }
                      }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatMoney(budget.spent, { decimals: 0 })} / {formatMoney(budget.limit, { decimals: 0 })}
                      </Typography>
                      <Typography variant="body2" fontWeight="600" color={budget.remaining >= 0 ? 'success.main' : 'error.main'}>
                        {budget.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                    
                    {budget.remaining > 0 ? (
                      <Typography variant="caption" color="success.main">
                        {formatMoney(budget.remaining, { decimals: 0 })} remaining
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="error.main">
                        {formatMoney(Math.abs(budget.remaining), { decimals: 0 })} over budget
                      </Typography>
                    )}

                    {/* Yearly view: Show pace */}
                    {activeView === 2 && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(6, 182, 212, 0.1)' }}>
                        <Typography variant="caption" display="block" sx={{ mb: 0.5, color: 'text.secondary' }}>
                          Spending Pace: {budget.pace.toFixed(1)}%
                        </Typography>
                        <Chip 
                          label={
                            budget.paceStatus === 'ahead' ? 'Ahead of Schedule' :
                            budget.paceStatus === 'on_track' ? 'On Track' :
                            'Behind Schedule'
                          }
                          size="small"
                          sx={{ 
                            backgroundColor: getPaceColor(budget.paceStatus),
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '20px'
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {budget.monthsElapsed} of 12 months elapsed
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Budget vs Actual Spending */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: 500, width: '100%' }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  Budget vs Actual Spending
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {currentData && currentData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={currentData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <defs>
                      <linearGradient id="budgetGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2196f3" stopOpacity={1} />
                        <stop offset="100%" stopColor="#64b5f6" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      tick={{ fontSize: 11, fill: '#334155' }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#334155' }}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      formatter={(value, name) => [formatMoney(value, { decimals: 0 }), name]}
                      contentStyle={{ 
                        fontSize: '12px',
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        borderRadius: '8px'
                      }}
                      itemStyle={{ color: '#ffffff' }}
                      labelStyle={{ color: '#ffffff', fontWeight: 'bold' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar 
                      dataKey="limit" 
                      fill="url(#budgetGradient)" 
                      name="Budget"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar 
                      dataKey="spent" 
                      name="Spent"
                      radius={[8, 8, 0, 0]}
                    >
                      {currentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No budget data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 2: Budget Utilization + First Trend Chart */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Budget Utilization (%) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: 500, width: '100%' }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  Budget Utilization (%)
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {currentData && currentData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={currentData} 
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis 
                      type="number" 
                      domain={[0, Math.max(120, ...currentData.map(d => d.percentage))]}
                      tick={{ fontSize: 11, fill: '#334155' }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      dataKey="name" 
                      type="category"
                      width={100}
                      tick={{ fontSize: 10, fill: '#334155' }}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${value.toFixed(1)}%`, name]}
                      contentStyle={{ 
                        fontSize: '12px',
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        borderRadius: '8px'
                      }}
                      itemStyle={{ color: '#ffffff' }}
                      labelStyle={{ color: '#ffffff', fontWeight: 'bold' }}
                    />
                    <Bar 
                      dataKey="percentage" 
                      name="Utilization %"
                      radius={[0, 8, 8, 0]}
                    >
                      {currentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No budget data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 12-Month Trend Chart with Category Selector */}
        {analytics.trends && analytics.trends.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: 500, width: '100%' }}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">12-Month Spending Trend</Typography>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        value={selectedTrendCategory}
                        onChange={(e) => setSelectedTrendCategory(e.target.value)}
                        displayEmpty
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {analytics.trends.map((trend, index) => (
                          <MenuItem key={index} value={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {trend.name || trend.category}
                              <Chip 
                                label={trend.category || 'Overall'}
                                size="small"
                                color={trend.category ? 'primary' : 'success'}
                                sx={{ fontSize: '0.65rem', height: '18px' }}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                }
                sx={{ pb: 0, pt: 1 }}
              />
              <CardContent sx={{ height: 420, p: 0, m: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={analytics.trends[selectedTrendCategory].trend}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <defs>
                      <linearGradient id="trendActualGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#ff9800" stopOpacity={1} />
                        <stop offset="100%" stopColor="#ffa726" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis 
                      dataKey="month" 
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      tick={{ fontSize: 10, fill: '#334155' }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#334155' }}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      formatter={(value, name) => [formatMoney(value, { decimals: 0 }), name]}
                      contentStyle={{ 
                        fontSize: '12px',
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        borderRadius: '8px'
                      }}
                      itemStyle={{ color: '#ffffff' }}
                      labelStyle={{ color: '#ffffff', fontWeight: 'bold' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="limit" 
                      stroke="#2196f3" 
                      name="Budget"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="spent" 
                      stroke="url(#trendActualGradient)" 
                      name="Actual"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#ff9800' }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default BudgetAnalytics;
